import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import GET_SUBMISSION_STATUSES_BY_CASES from '../../../../graphql/getSubmissionStatusesByCases';
import { INVENTORY_REQUEST_DETAIL_UI } from '../../../../util/constants';
import {
  shouldShowSubmissionInFlight,
  SubmissionStatusRecord,
} from '../../../../util/submissionStatus';

type SubmissionStatusByCaseRecord = SubmissionStatusRecord & {
  caseExternalId?: string | null;
};

interface UseSubmissionStatusesResult {
  submissionStatusByCase: Record<string, SubmissionStatusRecord>;
  error?: Error;
}

interface UseSubmissionStatusesOptions {
  // Poll only while some returned record is still on its way. Off by default, so callers that do
  // not ask for it keep polling whenever the query is enabled.
  pollOnlyWhileInFlight?: boolean;
}

const useSubmissionStatuses = (
  caseExternalIds: string[],
  enabled = true,
  { pollOnlyWhileInFlight = false }: UseSubmissionStatusesOptions = {}
): UseSubmissionStatusesResult => {
  const [isAnySubmissionInFlight, setIsAnySubmissionInFlight] = useState(false);
  const isQueryActive = enabled && caseExternalIds.length > 0;
  const shouldPoll = pollOnlyWhileInFlight
    ? isQueryActive && isAnySubmissionInFlight
    : isQueryActive;

  const { data: submissionStatusesData, error } = useQuery(GET_SUBMISSION_STATUSES_BY_CASES, {
    fetchPolicy: 'no-cache',
    skip: !enabled || caseExternalIds.length === 0,
    pollInterval: shouldPoll ? INVENTORY_REQUEST_DETAIL_UI.SUBMISSION_POLL_INTERVAL_MS : 0,
    variables: { caseExternalIds },
  });

  const submissionStatusByCase = useMemo(() => {
    const statusMap: Record<string, SubmissionStatusRecord> = {};
    (submissionStatusesData?.getSubmissionStatusesByCases || []).forEach(
      (entry: SubmissionStatusByCaseRecord) => {
        if (entry?.caseExternalId) {
          statusMap[entry.caseExternalId] = entry;
        }
      }
    );
    return statusMap;
  }, [submissionStatusesData]);

  useEffect(() => {
    if (!pollOnlyWhileInFlight) {
      return;
    }
    const anyInFlight =
      isQueryActive &&
      Object.values(submissionStatusByCase).some(record =>
        shouldShowSubmissionInFlight(
          record,
          INVENTORY_REQUEST_DETAIL_UI.SUBMISSION_RECOVERY_MAX_AGE_MS
        )
      );
    setIsAnySubmissionInFlight(anyInFlight);
  }, [pollOnlyWhileInFlight, isQueryActive, submissionStatusByCase]);

  return { submissionStatusByCase, error };
};

export default useSubmissionStatuses;
